import type { BIResponse } from '@/entities/bi'
import type { FilterSchema } from '@/shared/components/filter/validation'
import type { WidgetPeriods } from '../types'

import { any, last, pipe, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { useBIAnalyticQuery, zipSeries } from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'

import { Skeleton } from '@/shared/components/ui/skeleton'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

import PercentDifferenceBadge from '../../PercentDifferenceBadge'
import { Widget, WidgetContent, WidgetHeader } from '../../Widget'
import WidgetEmptyState from '../../WidgetEmptyState'
import WidgetErrorState from '../../WidgetErrorState'

const METRIC = 'accepted_total_percent'
const biCommonQuery = {
	metrics: [METRIC],
	dimensions: [],
	date_aggregation: null,
	label: 'NumericAcceptedRate',
}

export default function NumericAcceptedRateWidget({
	commonFilters,
	commonPeriods,
}: {
	commonPeriods: WidgetPeriods
	commonFilters: FilterSchema[]
}) {
	const { t } = useTranslation()

	const currency = useSelectedCurrency()
	const biComparedPeriodQuery = useBIAnalyticQuery<number | undefined>(
		{
			currency,
			...commonPeriods?.compared,
			...biCommonQuery,
			filters: commonFilters,
		},
		{ select: zipMetricValue, enabled: !!commonPeriods?.compared }
	)
	const biMainPeriodQuery = useBIAnalyticQuery<number | undefined>(
		{
			currency,
			...commonPeriods?.main,
			...biCommonQuery,
			filters: commonFilters,
		},
		{ select: zipMetricValue, enabled: !!commonPeriods?.main }
	)
	const isLoading = any(prop('isLoading'), [
		biMainPeriodQuery,
		biComparedPeriodQuery,
	])
	const isError = any(prop('isError'), [
		biMainPeriodQuery,
		biComparedPeriodQuery,
	])
	const mainValue = biMainPeriodQuery.data
	const comparedValue = biComparedPeriodQuery.data
	const hasValuesToCompare = !!mainValue && !!comparedValue

	return (
		<Widget className='relative truncate'>
			<WidgetHeader
				title={t('widget.accepted_rate.title', {
					defaultValue: 'Accepted Rate',
				})}
				tooltip={t('widget.accepted_rate.tooltip', {
					defaultValue: 'widget.accepted_rate.tooltip',
				})}
				subtitle={t('widget.accepted_rate.description', {
					defaultValue: 'widget.accepted_rate.description',
				})}
			/>
			<WidgetErrorState isTinted={isError} size='small' />
			<WidgetContent className='truncate'>
				{isLoading && (
					<div className='space-y-1'>
						<Skeleton className='col-span-2 h-6 w-24' />
						<Skeleton className='col-span-2 h-8 w-32' />
					</div>
				)}
				{mainValue && !isError && !isLoading && (
					<div className='space-y-1'>
						{hasValuesToCompare && (
							<PercentDifferenceBadge
								value={(mainValue * 100) / comparedValue - 100}
							/>
						)}
						<p className='truncate text-3xl font-semibold'>
							{formatNumber(mainValue ?? 0, NumberFormats.Percent)}
						</p>
					</div>
				)}
				{!mainValue && !isError && !isLoading && (
					<WidgetEmptyState size='small' />
				)}
			</WidgetContent>
		</Widget>
	)
}

const zipMetricValue = (data: BIResponse | null) => {
	if (!data?.data?.length) {
		return undefined
	}

	return pipe(
		(data) => zipSeries(data?.meta, data?.data),
		last,
		prop(METRIC)
	)(data)
}
